import { useState } from 'react'
import type { ChangeEvent } from 'react'
import { useIsMutating, useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, FileText, Trash2 } from 'lucide-react'
import {
  Banner,
  Button,
  FileDropzone,
  FocusEditor,
  ImageUploadField,
  NavigationLink,
  SegmentedControl,
  Skeleton,
  StatusBadge,
  Tooltip,
} from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import { AppError } from '@shared/api/error'
import type { MembersAreaDetail, MembersContentKind } from '../model/members-area.contract'
import {
  formatFileSize,
  getCompletenessPresentation,
  memberPreviewPath,
  publishBlockReason,
} from '../model/members-area.presentation'
import {
  putMembersAreaFile,
  useConfirmMembersAsset,
  useCreateMembersUploadUrl,
  useDeleteMembersAsset,
  useOrderMembersAssets,
  usePatchMembersContent,
  usePatchMembersCover,
} from '../model/members-area.mutations'
import { membersAreaDetailQuery } from '../model/members-area.queries'
import { CourseEditor } from './CourseEditor'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { ProgressBar } from './ProgressBar'
import {
  cascade,
  coverEditor,
  editorMain,
  editorStack,
  editorWorkspace,
  emptyEditor,
  fileList,
  fileMeta,
  fileRow,
  iconRow,
  kindBar,
  kindBarSide,
  muted,
} from './members-area.css'

const KIND_OPTIONS = [
  { value: 'course', label: 'Curso' },
  { value: 'files', label: 'Arquivos' },
] as const satisfies ReadonlyArray<{ value: MembersContentKind; label: string }>

const FILE_ACCEPT =
  '.pdf,.zip,.png,.jpg,.jpeg,.psd,application/pdf,application/zip,image/png,image/jpeg'
const MAX_ASSET_BYTES = 1_073_741_824

function errorMessage(error: unknown) {
  if (error instanceof AppError) return error.message
  if (error instanceof Error) return error.message
  return 'Não foi possível salvar.'
}

function notifyError(error: unknown) {
  toast.error(errorMessage(error))
}

function isEmptyContent(detail: MembersAreaDetail) {
  return detail.modules.length === 0 && detail.assets.length === 0
}

function completenessOf(detail: MembersAreaDetail) {
  if (detail.status === 'PUBLISHED') return 'published' as const
  if (isEmptyContent(detail)) return 'empty' as const
  return publishBlockReason(detail) ? ('incomplete' as const) : ('ready' as const)
}

function moveIds(ids: string[], id: string, direction: -1 | 1) {
  const index = ids.indexOf(id)
  const next = index + direction
  if (index < 0 || next < 0 || next >= ids.length) return null
  const copy = [...ids]
  const current = copy[index]
  const swap = copy[next]
  if (current === undefined || swap === undefined) return null
  copy[index] = swap
  copy[next] = current
  return copy
}

export function ContentEditor({
  productId,
  open,
  onClose,
}: {
  productId: string | null
  open: boolean
  onClose: () => void
}) {
  const id = productId ?? ''
  const detailQuery = useQuery(membersAreaDetailQuery(id))
  const detail = detailQuery.data
  const patchContent = usePatchMembersContent(id)
  const patchCover = usePatchMembersCover(id)
  const createUpload = useCreateMembersUploadUrl(id)
  const confirmAsset = useConfirmMembersAsset(id)
  const deleteAsset = useDeleteMembersAsset(id)
  const orderAssets = useOrderMembersAssets(id)
  const [uploads, setUploads] = useState<Record<string, number>>({})
  const [pendingAsset, setPendingAsset] = useState<{ id: string; name: string } | null>(null)

  const saving = useIsMutating() > 0
  const blockReason = detail ? publishBlockReason(detail) : null
  const completeness = detail ? completenessOf(detail) : 'empty'
  const status = getCompletenessPresentation(completeness)
  const StatusIcon = status.icon
  const canChangeKind = detail ? isEmptyContent(detail) : false

  async function handlePublish() {
    if (!detail) return
    try {
      await patchContent.mutateAsync({
        status: detail.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED',
      })
      toast.success(
        detail.status === 'PUBLISHED' ? 'Conteúdo despublicado.' : 'Conteúdo publicado.',
      )
    } catch (error) {
      notifyError(error)
    }
  }

  async function handleCoverChange(coverImage: string) {
    if (!detail || coverImage === detail.coverImage) return
    try {
      await patchCover.mutateAsync(coverImage)
    } catch (error) {
      notifyError(error)
    }
  }

  async function handleKindChange(kind: MembersContentKind) {
    if (!detail || kind === detail.kind) return
    try {
      await patchContent.mutateAsync({ kind })
    } catch (error) {
      notifyError(error)
    }
  }

  async function handleFiles(fileList: FileList | null, lessonId?: string) {
    if (!fileList) return
    for (const file of Array.from(fileList)) {
      if (file.size > MAX_ASSET_BYTES) {
        toast.error(`"${file.name}" passa de 1 GB.`)
        continue
      }
      const key = `${file.name}-${file.size}`
      try {
        setUploads((current) => ({ ...current, [key]: 10 }))
        const upload = await createUpload.mutateAsync({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          ...(lessonId ? { lessonId } : {}),
        })
        setUploads((current) => ({ ...current, [key]: 55 }))
        await putMembersAreaFile(upload, file, id)
        setUploads((current) => ({ ...current, [key]: 85 }))
        await confirmAsset.mutateAsync(upload.id)
        setUploads((current) => ({ ...current, [key]: 100 }))
      } catch (error) {
        notifyError(error)
      } finally {
        setUploads((current) => {
          const next = { ...current }
          delete next[key]
          return next
        })
      }
    }
  }

  function askDeleteAsset(assetId: string) {
    const asset =
      detail?.assets.find((item) => item.id === assetId) ??
      detail?.modules
        .flatMap((module) => module.lessons)
        .flatMap((lesson) => lesson.assets)
        .find((item) => item.id === assetId)
    setPendingAsset({ id: assetId, name: asset?.name ?? 'este arquivo' })
  }

  async function confirmDeleteAsset() {
    if (!pendingAsset) return
    try {
      await deleteAsset.mutateAsync(pendingAsset.id)
      setPendingAsset(null)
    } catch (error) {
      notifyError(error)
    }
  }

  async function handleMoveAsset(assetId: string, direction: -1 | 1, lessonId?: string) {
    if (!detail) return
    const assets = lessonId
      ? (detail.modules.flatMap((module) => module.lessons).find((item) => item.id === lessonId)
          ?.assets ?? [])
      : detail.assets.filter((asset) => asset.lessonId === null)
    const orderedIds = moveIds(
      assets.map((asset) => asset.id),
      assetId,
      direction,
    )
    if (!orderedIds) return
    try {
      await orderAssets.mutateAsync({
        orderedIds,
        ...(lessonId ? { lessonId } : {}),
      })
    } catch (error) {
      notifyError(error)
    }
  }

  const published = detail?.status === 'PUBLISHED'
  const primaryDisabled = saving || (!published && Boolean(blockReason))

  return (
    <FocusEditor
      open={open}
      onClose={onClose}
      title={detail?.name ?? 'Conteúdo'}
      breadcrumb="Área de membros"
      saveState={saving ? 'saving' : 'saved'}
      primaryLabel={published ? 'Despublicar' : 'Publicar'}
      onPrimaryAction={() => void handlePublish()}
      primaryDisabled={primaryDisabled}
    >
      <div className={cascade}>
        {detailQuery.isPending ? <Skeleton height={320} radius="16px" /> : null}
        {detailQuery.isError ? (
          <Banner tone="danger" title="Não foi possível carregar o conteúdo">
            Tente fechar e abrir de novo.
          </Banner>
        ) : null}
        {detail ? (
          <div className={kindBar}>
            {canChangeKind ? (
              <SegmentedControl
                value={detail.kind}
                options={KIND_OPTIONS}
                onChange={(kind) => void handleKindChange(kind)}
                ariaLabel="Tipo do conteúdo"
              />
            ) : (
              <Tooltip content="O tipo só muda enquanto o conteúdo estiver vazio." placement="top">
                <SegmentedControl
                  value={detail.kind}
                  options={KIND_OPTIONS}
                  disabled
                  onChange={(kind) => void handleKindChange(kind)}
                  ariaLabel="Tipo do conteúdo"
                />
              </Tooltip>
            )}
            <div className={kindBarSide}>
              <StatusBadge
                tone={status.tone}
                label={status.label}
                icon={<StatusIcon size={13} />}
                size="sm"
              />
              <NavigationLink href="/produtos">Editar produto</NavigationLink>
              {published ? (
                <NavigationLink href={memberPreviewPath(id)} target="_blank" rel="noreferrer">
                  Ver como membro
                </NavigationLink>
              ) : null}
            </div>
          </div>
        ) : null}
        {detail && blockReason && !published ? (
          <Banner tone="warning" title="Ainda não dá para publicar">
            {blockReason}
          </Banner>
        ) : null}
        {detail ? (
          <div className={editorWorkspace}>
            <div className={coverEditor}>
              <ImageUploadField
                label="Capa do produto"
                kind="cover"
                value={detail.coverImage}
                onChange={(coverImage) => void handleCoverChange(coverImage)}
                aspect="1 / 1"
                maxInlineSize={180}
                allowUrl
              />
            </div>
            <div className={editorMain}>
              {detail.kind === 'course' ? (
                <CourseEditor
                  productId={id}
                  detail={detail}
                  onLessonFiles={(files, lessonId) => void handleFiles(files, lessonId)}
                  renderLessonAssets={(lessonId, assets) => (
                    <AssetList
                      assets={assets}
                      onDelete={askDeleteAsset}
                      onMove={(assetId, direction) =>
                        void handleMoveAsset(assetId, direction, lessonId)
                      }
                    />
                  )}
                />
              ) : null}
              {detail.kind === 'files' ? (
                <FilesEditor
                  detail={detail}
                  uploads={uploads}
                  onFiles={(files) => void handleFiles(files)}
                  onDelete={askDeleteAsset}
                  onMove={(assetId, direction) => void handleMoveAsset(assetId, direction)}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <DeleteConfirmDialog
        open={pendingAsset !== null}
        title="Remover este arquivo?"
        description={pendingAsset ? `"${pendingAsset.name}" deixa de fazer parte da entrega.` : ''}
        confirmLabel="Remover arquivo"
        pending={deleteAsset.isPending}
        onCancel={() => setPendingAsset(null)}
        onConfirm={() => void confirmDeleteAsset()}
      />
    </FocusEditor>
  )
}

function FilesEditor({
  detail,
  uploads,
  onFiles,
  onDelete,
  onMove,
}: {
  detail: MembersAreaDetail
  uploads: Record<string, number>
  onFiles: (files: FileList | null) => void
  onDelete: (assetId: string) => void
  onMove: (assetId: string, direction: -1 | 1) => void
}) {
  const files = detail.assets.filter((asset) => asset.lessonId === null)
  return (
    <div className={editorStack} data-testid="members-files-editor">
      <FileDropzone
        id="members-files-upload"
        label="Arquivos"
        accept={FILE_ACCEPT}
        multiple
        helperText="PDF, ZIP, PNG, JPG ou PSD. Até 1 GB cada."
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          onFiles(event.target.files)
          event.target.value = ''
        }}
      />
      {Object.entries(uploads).map(([key, value]) => (
        <div key={key}>
          <p className={muted}>{key}</p>
          <ProgressBar value={value} label={`Envio de ${key}`} />
        </div>
      ))}
      {files.length === 0 ? (
        <div className={emptyEditor}>
          <FileText size={20} aria-hidden />
          <span>Nenhum arquivo ainda. Envie o material que a compra libera para download.</span>
        </div>
      ) : (
        <AssetList assets={files} onDelete={onDelete} onMove={onMove} />
      )}
    </div>
  )
}

function AssetList({
  assets,
  onDelete,
  onMove,
}: {
  assets: MembersAreaDetail['assets']
  onDelete: (assetId: string) => void
  onMove: (assetId: string, direction: -1 | 1) => void
}) {
  return (
    <div className={fileList}>
      {assets.map((asset, index) => (
        <div key={asset.id} className={fileRow} data-testid={`members-asset-${asset.id}`}>
          <div className={fileMeta}>
            <strong>{asset.name}</strong>
            <span className={muted}>
              {asset.contentType} · {formatFileSize(asset.sizeBytes)} ·{' '}
              {asset.status === 'READY' ? 'Pronto' : 'Enviando'}
            </span>
          </div>
          <div className={iconRow}>
            <Button
              variant="text"
              size="sm"
              aria-label={`Subir ${asset.name}`}
              disabled={index === 0}
              onClick={() => onMove(asset.id, -1)}
            >
              <ChevronUp size={16} />
            </Button>
            <Button
              variant="text"
              size="sm"
              aria-label={`Descer ${asset.name}`}
              disabled={index === assets.length - 1}
              onClick={() => onMove(asset.id, 1)}
            >
              <ChevronDown size={16} />
            </Button>
            <Button
              variant="text"
              size="sm"
              aria-label={`Remover ${asset.name}`}
              onClick={() => onDelete(asset.id)}
            >
              <Trash2 size={16} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}
